import React, { useState, useEffect, useRef } from 'react';
import axios from '@/utils/axiosConfig';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, Plus, Download, Calendar, TrendingUp, User, CheckCircle, Clock, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const Paie = ({ user, token, API }) => {
  const [activeSection, setActiveSection] = useState('salaries'); // salaries, benefits
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [stats, setStats] = useState({
    totalPaid: 0,
    avgSalary: 0,
    pendingPayments: 0
  });
  
  // Salary Form
  const [salaryForm, setSalaryForm] = useState({
    employee_id: '',
    month: '',
    base_salary: 0,
    bonuses: 0,
    deductions: 0,
    overtime_hours: 0,
    overtime_pay: 0,
    notes: ''
  });
  
  // Benefit Form
  const [benefitForm, setBenefitForm] = useState({
    employee_id: '',
    benefit_type: 'assurance_sante',
    description: '',
    value: 0,
    start_date: '',
    end_date: ''
  });

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeSection]);

  const fetchData = async () => {
    try {
      if (isMountedRef.current) setLoading(true);
      
      if (activeSection === 'salaries') {
        const response = await axios.get(`${API}/salary-records`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (isMountedRef.current) {
          setSalaryRecords(response.data);
          
          // Calculate stats
          const paid = response.data.filter(r => r.payment_status === 'paye');
          const totalPaid = paid.reduce((sum, r) => sum + r.net_salary, 0);
          const avgSalary = paid.length > 0 ? totalPaid / paid.length : 0;
          const pendingPayments = response.data.filter(r => r.payment_status === 'en_attente').length;
          
          setStats({ totalPaid, avgSalary, pendingPayments });
        }
      } else if (activeSection === 'benefits') {
        const response = await axios.get(`${API}/benefits`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (isMountedRef.current) setBenefits(response.data);
      }
      
      // Fetch employees if admin
      if (user.role === 'admin') {
        const usersResponse = await axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmployees(usersResponse.data.filter(u => u.role === 'employee'));
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement des données');
      setLoading(false);
    }
  };

  const handleCreateSalaryRecord = async () => {
    if (!salaryForm.employee_id || !salaryForm.month || salaryForm.base_salary <= 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API}/salary-records`, salaryForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Enregistrement de paie créé avec succès');
      setDialogOpen(false);
      setSalaryForm({
        employee_id: '',
        month: '',
        base_salary: 0,
        bonuses: 0,
        deductions: 0,
        overtime_hours: 0,
        overtime_pay: 0,
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCreateBenefit = async () => {
    if (!benefitForm.employee_id || !benefitForm.description || !benefitForm.start_date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API}/benefits`, benefitForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Avantage créé avec succès');
      setDialogOpen(false);
      setBenefitForm({
        employee_id: '',
        benefit_type: 'assurance_sante',
        description: '',
        value: 0,
        start_date: '',
        end_date: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownloadPayslip = async (recordId, employeeName, month) => {
    try {
      const response = await axios.get(`${API}/salary-records/${recordId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulletin_paie_${employeeName.replace(' ', '_')}_${month}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Bulletin de paie téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleUpdatePaymentStatus = async (recordId, status) => {
    try {
      await axios.put(`${API}/salary-records/${recordId}`, {
        payment_status: status,
        payment_date: status === 'paye' ? new Date().toISOString().split('T')[0] : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getBenefitTypeLabel = (type) => {
    const types = {
      assurance_sante: '🏥 Assurance Santé',
      conges_payes: '🏖️ Congés Payés',
      retraite: '👴 Retraite',
      autre: '📋 Autre'
    };
    return types[type] || types.autre;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics (Salaries only) */}
      {activeSection === 'salaries' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Payé (Année)</p>
                  <p className="text-3xl font-bold text-green-600">
                    {stats.totalPaid.toLocaleString('fr-MA')} MAD
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-green-600">
                  <DollarSign className="h-8 w-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Salaire Moyen</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {stats.avgSalary.toLocaleString('fr-MA', { maximumFractionDigits: 0 })} MAD
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-blue-600">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-50 to-yellow-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Paiements en Attente</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {stats.pendingPayments}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-orange-600">
                  <Clock className="h-8 w-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { id: 'salaries', name: '💰 Salaires', count: salaryRecords.length },
          { id: 'benefits', name: '🎁 Avantages', count: benefits.length }
        ].map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
              activeSection === section.id
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {section.name} ({section.count})
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          {activeSection === 'salaries' && '💰 Historique des Salaires'}
          {activeSection === 'benefits' && '🎁 Avantages Sociaux'}
        </h2>

        {user.role === 'admin' && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-green-600 to-emerald-600">
                <Plus className="mr-2 h-4 w-4" />
                {activeSection === 'salaries' && 'Nouveau Salaire'}
                {activeSection === 'benefits' && 'Nouvel Avantage'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {activeSection === 'salaries' && 'Créer un Enregistrement de Salaire'}
                  {activeSection === 'benefits' && 'Créer un Avantage Social'}
                </DialogTitle>
              </DialogHeader>

              {activeSection === 'salaries' && (
                <div className="space-y-4">
                  <div>
                    <Label>Employé *</Label>
                    <Select value={salaryForm.employee_id} onValueChange={(val) => setSalaryForm({...salaryForm, employee_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un employé" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Mois (YYYY-MM) *</Label>
                    <Input type="month" value={salaryForm.month} onChange={(e) => setSalaryForm({...salaryForm, month: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Salaire de Base (MAD) *</Label>
                      <Input type="number" step="0.01" value={salaryForm.base_salary} onChange={(e) => setSalaryForm({...salaryForm, base_salary: parseFloat(e.target.value)})} />
                    </div>
                    <div>
                      <Label>Primes (MAD)</Label>
                      <Input type="number" step="0.01" value={salaryForm.bonuses} onChange={(e) => setSalaryForm({...salaryForm, bonuses: parseFloat(e.target.value)})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Heures Supplémentaires</Label>
                      <Input type="number" step="0.5" value={salaryForm.overtime_hours} onChange={(e) => setSalaryForm({...salaryForm, overtime_hours: parseFloat(e.target.value)})} />
                    </div>
                    <div>
                      <Label>Paiement Heures Sup. (MAD)</Label>
                      <Input type="number" step="0.01" value={salaryForm.overtime_pay} onChange={(e) => setSalaryForm({...salaryForm, overtime_pay: parseFloat(e.target.value)})} />
                    </div>
                  </div>
                  <div>
                    <Label>Déductions (MAD)</Label>
                    <Input type="number" step="0.01" value={salaryForm.deductions} onChange={(e) => setSalaryForm({...salaryForm, deductions: parseFloat(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea value={salaryForm.notes} onChange={(e) => setSalaryForm({...salaryForm, notes: e.target.value})} rows={2} />
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm font-semibold text-blue-900 mb-1">Salaire Net:</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {(
                        (salaryForm.base_salary || 0) +
                        (salaryForm.bonuses || 0) +
                        (salaryForm.overtime_pay || 0) -
                        (salaryForm.deductions || 0)
                      ).toFixed(2)} MAD
                    </p>
                  </div>
                  <Button onClick={handleCreateSalaryRecord} className="w-full bg-gradient-to-r from-green-600 to-emerald-600">
                    Créer l'Enregistrement
                  </Button>
                </div>
              )}

              {activeSection === 'benefits' && (
                <div className="space-y-4">
                  <div>
                    <Label>Employé *</Label>
                    <Select value={benefitForm.employee_id} onValueChange={(val) => setBenefitForm({...benefitForm, employee_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un employé" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Type d'Avantage *</Label>
                    <Select value={benefitForm.benefit_type} onValueChange={(val) => setBenefitForm({...benefitForm, benefit_type: val})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="assurance_sante">🏥 Assurance Santé</SelectItem>
                        <SelectItem value="conges_payes">🏖️ Congés Payés</SelectItem>
                        <SelectItem value="retraite">👴 Retraite</SelectItem>
                        <SelectItem value="autre">📋 Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Description *</Label>
                    <Textarea value={benefitForm.description} onChange={(e) => setBenefitForm({...benefitForm, description: e.target.value})} rows={3} />
                  </div>
                  <div>
                    <Label>Valeur (MAD) - Optionnel</Label>
                    <Input type="number" step="0.01" value={benefitForm.value} onChange={(e) => setBenefitForm({...benefitForm, value: parseFloat(e.target.value)})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date de Début *</Label>
                      <Input type="date" value={benefitForm.start_date} onChange={(e) => setBenefitForm({...benefitForm, start_date: e.target.value})} />
                    </div>
                    <div>
                      <Label>Date de Fin</Label>
                      <Input type="date" value={benefitForm.end_date} onChange={(e) => setBenefitForm({...benefitForm, end_date: e.target.value})} />
                    </div>
                  </div>
                  <Button onClick={handleCreateBenefit} className="w-full bg-gradient-to-r from-green-600 to-emerald-600">
                    Créer l'Avantage
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Content */}
      <div className="space-y-4">
        {/* Salaries Section */}
        {activeSection === 'salaries' && (
          salaryRecords.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <DollarSign className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-lg text-gray-500">Aucun enregistrement de salaire</p>
              </CardContent>
            </Card>
          ) : (
            salaryRecords.map(record => (
              <Card key={record.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {(user.role === 'admin' || user.role === 'sub_admin') && (
                          <div className="flex items-center gap-2">
                            <User className="h-5 w-5 text-gray-500" />
                            <h3 className="text-lg font-semibold text-gray-900">{record.employee_name}</h3>
                          </div>
                        )}
                        <Badge className="bg-blue-100 text-blue-800">
                          {format(parseISO(record.month + '-01'), 'MMMM yyyy', { locale: fr })}
                        </Badge>
                        <Badge className={record.payment_status === 'paye' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                          {record.payment_status === 'paye' ? '✓ Payé' : '⏳ En Attente'}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Salaire de Base</p>
                          <p className="text-sm font-semibold text-gray-900">{record.base_salary.toLocaleString('fr-MA')} MAD</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Primes</p>
                          <p className="text-sm font-semibold text-gray-900">{record.bonuses.toLocaleString('fr-MA')} MAD</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Déductions</p>
                          <p className="text-sm font-semibold text-red-600">-{record.deductions.toLocaleString('fr-MA')} MAD</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Net à Payer</p>
                          <p className="text-lg font-bold text-green-600">{record.net_salary.toLocaleString('fr-MA')} MAD</p>
                        </div>
                      </div>
                      
                      {record.overtime_hours > 0 && (
                        <p className="text-xs text-gray-600 mb-2">
                          Heures sup: {record.overtime_hours}h → {record.overtime_pay.toLocaleString('fr-MA')} MAD
                        </p>
                      )}
                      
                      {record.notes && (
                        <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">{record.notes}</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-red-50 hover:bg-red-100 text-red-600 border-red-200"
                        onClick={() => handleDownloadPayslip(record.id, record.employee_name, record.month)}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                      
                      {user.role === 'admin' && record.payment_status === 'en_attente' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleUpdatePaymentStatus(record.id, 'paye')}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Marquer Payé
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )
        )}

        {/* Benefits Section */}
        {activeSection === 'benefits' && (
          benefits.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Gift className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-lg text-gray-500">Aucun avantage social</p>
              </CardContent>
            </Card>
          ) : (
            benefits.map(benefit => (
              <Card key={benefit.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {(user.role === 'admin' || user.role === 'sub_admin') && (
                          <div className="flex items-center gap-2">
                            <User className="h-5 w-5 text-gray-500" />
                            <h3 className="text-lg font-semibold text-gray-900">{benefit.employee_name}</h3>
                          </div>
                        )}
                        <Badge className="bg-purple-100 text-purple-800">
                          {getBenefitTypeLabel(benefit.benefit_type)}
                        </Badge>
                        <Badge className={benefit.status === 'actif' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {benefit.status === 'actif' ? 'Actif' : 'Expiré'}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-3">{benefit.description}</p>
                      
                      <div className="flex items-center gap-6 text-xs text-gray-600">
                        {benefit.value && (
                          <div>
                            <span className="font-semibold">Valeur: </span>
                            {benefit.value.toLocaleString('fr-MA')} MAD
                          </div>
                        )}
                        <div>
                          <span className="font-semibold">Début: </span>
                          {format(parseISO(benefit.start_date), 'dd/MM/yyyy', { locale: fr })}
                        </div>
                        {benefit.end_date && (
                          <div>
                            <span className="font-semibold">Fin: </span>
                            {format(parseISO(benefit.end_date), 'dd/MM/yyyy', { locale: fr })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )
        )}
      </div>
    </div>
  );
};

export default Paie;
